import { getSchemaValidator } from 'schemas';
// The transform output is wrapped into an inline Dataset (via DataWrapper.inline)
// so the registry entry exposes the same `.datasource` / `.query()` surface as
// any other data source. Imported directly from './data-model' (same as
// data-provider) since it is not re-exported on the framework barrel.
import { DataWrapper } from './data-model';
// usesEventInput detects whether a transform's code reads $event; the data layer
// uses it to give event-using transforms a fresh registry identity per event so
// consumer caches (BaseWorldLayer.cachedQuery) re-run them on each interaction.
import { usesEventInput } from './transform-event';
// EID-125: per-input Datamesh query specs. normalizeTransformInputs shims
// legacy string inputs and fail-loud validates query forms the client engine
// cannot execute; rewriteInputQuery merges each input's spec over the
// propagated consumer query before the input container is queried.
import {
  normalizeTransformInputs,
  rewriteInputQuery,
  TransformInputSpec,
} from './transform-input-query';
// EID-126: each code version is compiled as a real ES module (imports per
// modules alias + exported async function) loaded via import(blobUrl) in the
// {type: 'module'} worker, replacing new Function — real line numbers,
// devtools-debuggable, and warm recompiles re-import modules from the browser
// module cache.
import {
  buildTransformModuleSource,
  TransformModuleSource,
} from './transform-module-source';
// EID-126: lifecycle status reported through the onStatus init option; the
// data layer publishes it per transform id so UIs can surface failures that
// previously only reached the console.
import {
  errorStatus,
  nextTransformStatus,
  TransformStatus,
  TransformStatusEvent,
} from './transform-status';

// Define DataContainer interface locally if not imported, for type safety
interface DataContainer {
  query(queryObj: any): Promise<any>;
  // Add other relevant DataContainer properties/methods if needed for type checking
}

// --- Message Interfaces ---
// EID-126: the old single `init` message (modules + code) is split into
// `init` (module imports only) and `setCode` (one message per code version),
// so a code-only spec change recompiles in the live worker without teardown
// or module re-import.
interface WorkerInitMessage {
  type: 'init';
  moduleMap: Record<string, string>;
}

interface WorkerSetCodeMessage {
  type: 'setCode';
  setCodeId: number;
  // Blob URL of the generated ES module for this code version. Created (and
  // revoked) on the main thread so terminate() can always release it.
  moduleUrl: string;
}

interface WorkerExecuteQueryMessage {
  type: 'query';
  queryId: string;
  inputData: Record<string, any>; // Resolved data from DataContainers, keyed by sourceId, plus the reserved '$event' key (EID-86)
  queryObj: any; // The original query object passed to Transform.query
  outputType: string;
}

type ToWorkerMessage =
  WorkerInitMessage | WorkerSetCodeMessage | WorkerExecuteQueryMessage;

interface WorkerInitReadyResponseMessage {
  type: 'init_ready';
}

interface WorkerInitErrorResponseMessage {
  type: 'init_error';
  error: string;
  stack?: string;
}

interface WorkerCodeReadyResponseMessage {
  type: 'code_ready';
  setCodeId: number;
}

interface WorkerCodeErrorResponseMessage {
  type: 'code_error';
  setCodeId: number;
  error: string;
  stack?: string;
}

// A setCode superseded by a newer one before its import finished; the worker
// discarded it without swapping the compiled function in.
interface WorkerCodeDiscardedResponseMessage {
  type: 'code_discarded';
  setCodeId: number;
}

interface WorkerQueryResponseMessage {
  type: 'query_response';
  queryId: string;
  result?: any;
  error?: string;
  details?: any;
  stack?: string;
}

type FromWorkerMessage =
  | WorkerInitReadyResponseMessage
  | WorkerInitErrorResponseMessage
  | WorkerCodeReadyResponseMessage
  | WorkerCodeErrorResponseMessage
  | WorkerCodeDiscardedResponseMessage
  | WorkerQueryResponseMessage;

// --- Pending Query Interface ---
interface PendingQuery {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timer: number;
}

// --- Pending setCode (EID-126) ---
interface PendingSetCode {
  // Blob URL of this code version's module, revoked unless it commits.
  url: string;
  code: string;
  codeLineOffset: number;
  resolve: (ok: boolean) => void;
}

// --- Options accepted by Transform.init ---
export interface TransformInitOpts {
  // EID-86: lazy accessor for the current map interaction event.
  getEvent?: () => any;
  // EID-126: lifecycle status callback; receives every status transition
  // (initializing / importing-modules / ready / error{phase}).
  onStatus?: (status: TransformStatus) => void;
}

// --- TransformSpecification for init method ---
export interface InitTransformSpec {
  id: string;
  code: string;
  // Per-input Datamesh query specs ({ datasource: <source id>, ...query
  // overrides }); the datasource values are also the named keys in the
  // '$inputs' object for the transform function. Legacy bare source-id
  // strings are normalized to { datasource } with a deprecation warning
  // (EID-125).
  inputs: (string | TransformInputSpec)[];
  modules?: Record<string, string>;
  outputType?: string;
}

// EID-86: re-exported so the data layer can gate the event→re-query
// invalidation on whether a transform references `$event`. Defined in its own
// import-free module so it stays unit-testable barrel-free (this file pulls in
// the `schemas` package, which tests avoid).
export { usesEventInput } from './transform-event';

export class Transform {
  private id: string;
  private code: string;
  private modules: Record<string, string>;
  private outputType: string;
  // Resolved input data containers, index-aligned with `inputSpecs` (the
  // normalized per-input query specs from spec.inputs) and `inputIds` (their
  // datasource values). Each input is exposed to the transform code both
  // positionally and by source id (see query()).
  private resolvedInputsMap: DataContainer[];
  private inputSpecs: TransformInputSpec[];
  private inputIds: string[];
  // EID-86: accessor for the current map interaction event, supplied at init.
  private getEvent?: () => any;
  // EID-126: status transition sink supplied at init (see reportStatus).
  private onStatus?: (status: TransformStatus) => void;
  private status: TransformStatus = { state: 'initializing' };

  // The transform output wrapped into an inline Dataset. `datasource` is the
  // Dataset of the most recent (or seed) materialisation — consumers that read
  // coordinate metadata off the registry entry (gridded/seasurface/track layers)
  // use `currentData.datasource.coordkeys`, matching the DataWrapper surface.
  // `refresh` mirrors DataWrapper.refresh so those consumers can treat a
  // transform exactly like any other data source.
  public datasource: any = null;
  public refresh = false;
  // Whether the transform's code reads $event. Event-using transforms must be
  // re-run on every map interaction; the data layer keys their registry
  // identity on the current event so cachedQuery re-queries them.
  // EID-126: updated when a warm recompile commits new code.
  public usesEvent = false;

  private worker: Worker | null = null;
  private workerUrl: string | null = null;
  private pendingQueries: Map<string, PendingQuery> = new Map();
  private nextQueryId = 0;
  private readonly QUERY_TIMEOUT_MS = 30000;
  private workerReady: boolean = false;

  // EID-126: warm-recompile state. Every code version gets its own blob URL
  // (created main-side so terminate() can release them all); `codeModuleUrl`
  // is the committed version, `liveCodeUrls` every not-yet-revoked URL,
  // `latestSetCodeId` the authoritative in-flight compile (older ones are
  // superseded and discarded).
  private codeModuleUrl: string | null = null;
  private liveCodeUrls: Set<string> = new Set();
  private pendingSetCodes: Map<number, PendingSetCode> = new Map();
  private nextSetCodeId = 1;
  private latestSetCodeId = 0;
  // Generated lines preceding user code in the committed module (import
  // statements + export wrapper); lets error statuses carry the offset needed
  // to map worker stack-frame lines back to editor lines.
  private codeLineOffset = 0;

  private constructor(
    spec: InitTransformSpec,
    inputSpecs: TransformInputSpec[],
    resolvedInputsMap: DataContainer[],
    opts?: TransformInitOpts,
  ) {
    this.id = spec.id;
    this.code = spec.code;
    this.usesEvent = usesEventInput(spec.code);
    this.modules = spec.modules || {};
    this.outputType = spec.outputType || 'dataset';
    this.resolvedInputsMap = resolvedInputsMap;
    // Pre-normalized by init (normalizeTransformInputs).
    this.inputSpecs = inputSpecs;
    this.inputIds = inputSpecs.map((input) => input.datasource);
    this.getEvent = opts?.getEvent;
    this.onStatus = opts?.onStatus;
    this.onStatus?.(this.status);

    // EID-126: the worker no longer compiles code itself — it imports the
    // per-code-version ES module (blob URL) the main thread hands it, keeping
    // the split protocol: `init` imports the CDN modules (in parallel, warming
    // the module cache so the generated module's static imports are cache
    // hits), `setCode` swaps the compiled function without teardown, `query`
    // invokes the current function.
    const workerScript = `
      let transformFn = null;
      let moduleImportError = null;
      let latestSetCodeId = 0;

      self.onmessage = async (event) => {
        const msg = event.data;

        if (msg.type === 'init') {
          try {
            await Promise.all(
              Object.entries(msg.moduleMap).map(([alias, url]) =>
                import(url).catch((e) => {
                  console.error('Worker: Error importing module ' + alias + ' (' + url + '):', e);
                  throw new Error('Module import failed for ' + alias + ': ' + e.message);
                }),
              ),
            );
            self.postMessage({ type: 'init_ready' });
          } catch (e) {
            moduleImportError = e.message;
            console.error('Worker: Initialization failed:', e);
            self.postMessage({ type: 'init_error', error: e.message, stack: e.stack });
          }
          return;
        }

        if (msg.type === 'setCode') {
          latestSetCodeId = msg.setCodeId;
          try {
            const mod = await import(msg.moduleUrl);
            if (msg.setCodeId !== latestSetCodeId) {
              // A newer setCode arrived while this import was in flight.
              self.postMessage({ type: 'code_discarded', setCodeId: msg.setCodeId });
              return;
            }
            if (typeof mod.default !== 'function') {
              throw new Error('Transform module must default-export a function.');
            }
            transformFn = mod.default;
            self.postMessage({ type: 'code_ready', setCodeId: msg.setCodeId });
          } catch (e) {
            if (msg.setCodeId !== latestSetCodeId) {
              self.postMessage({ type: 'code_discarded', setCodeId: msg.setCodeId });
            } else {
              console.error('Worker: Code compilation failed:', e);
              self.postMessage({ type: 'code_error', setCodeId: msg.setCodeId, error: e.message, stack: e.stack });
            }
          }
          return;
        }

        if (msg.type === 'query') {
          if (!transformFn) {
            const reason = moduleImportError
              ? 'Worker initialization failed: ' + moduleImportError
              : 'Worker not ready or initialization incomplete.';
            self.postMessage({ type: 'query_response', queryId: msg.queryId, error: reason });
            return;
          }
          try {
            const result = await transformFn(msg.inputData, msg.queryObj, msg.outputType);
            self.postMessage({ type: 'query_response', queryId: msg.queryId, result });
          } catch (e) {
            console.error('Worker: Error executing user code for query ' + msg.queryId + ':', e);
            self.postMessage({ type: 'query_response', queryId: msg.queryId, error: e.message, stack: e.stack });
          }
          return;
        }
      };
    `;

    try {
      const blob = new Blob([workerScript], { type: 'application/javascript' });
      this.workerUrl = URL.createObjectURL(blob);
      // EID-126: a module worker, so the per-code-version ES modules (and
      // their static imports) can be loaded with import().
      this.worker = new Worker(this.workerUrl, { type: 'module' });
      // Temporary handlers for init phase are set up in the static init method's Promise
    } catch (e) {
      console.error(`Failed to create worker for transform '${this.id}':`, e);
      if (this.workerUrl) URL.revokeObjectURL(this.workerUrl);
      this.worker = null;
      this.workerUrl = null;
      // This error will be caught by the Promise in the static init method
      throw e;
    }
  }

  // EID-126: apply a status transition through the pure state machine and
  // notify the onStatus sink. Illegal transitions return the same object
  // identity and are not re-emitted.
  private reportStatus(event: TransformStatusEvent): void {
    const next = nextTransformStatus(this.status, event);
    if (next === this.status) return;
    this.status = next;
    this.onStatus?.(next);
  }

  public static init(
    spec: InitTransformSpec,
    inputDataContainers: DataContainer[],
    opts?: TransformInitOpts,
  ): Promise<Transform | null> {
    // EID-126: pre-instance failures previously resolved null silently
    // (console only); they now emit an error status through the same sink.
    const emitInitError = (message: string) =>
      opts?.onStatus?.(errorStatus('init', message));

    if (!spec.code) {
      console.error(`Transform '${spec.id}' declared without code.`);
      emitInitError(`Transform '${spec.id}' declared without code.`);
      return Promise.resolve(null);
    }

    // EID-125: normalize legacy string inputs to { datasource } and fail loud
    // (thrown descriptive error, surfaced here) on Datamesh query forms the
    // client engine cannot execute yet — the transform is not created.
    // warnings: false — the data provider already normalized-and-warned once
    // per watcher; init re-runs on every worker (re)init and would otherwise
    // repeat the deprecation/duplicate warnings each time.
    let inputSpecs: TransformInputSpec[];
    try {
      inputSpecs = normalizeTransformInputs(spec.inputs, { warnings: false });
    } catch (e: any) {
      console.error(`Transform '${spec.id}' has invalid inputs:`, e);
      emitInitError(
        `Transform '${spec.id}' has invalid inputs: ${e?.message ?? e}`,
      );
      return Promise.resolve(null);
    }

    if (inputSpecs.length !== inputDataContainers.length) {
      const message = `Transform '${spec.id}': Mismatch between specified inputs (${inputSpecs.length}) and provided data containers (${inputDataContainers.length}).`;
      console.error(message);
      emitInitError(message);
      return Promise.resolve(null);
    }

    let transformInstance: Transform;
    try {
      transformInstance = new Transform(
        spec,
        inputSpecs,
        inputDataContainers,
        opts,
      );
    } catch (e: any) {
      console.error(`Transform '${spec.id}' constructor failed:`, e);
      emitInitError(
        `Transform '${spec.id}' constructor failed: ${e?.message ?? e}`,
      );
      return Promise.resolve(null);
    }

    return new Promise<Transform | null>((resolve) => {
      if (!transformInstance.worker) {
        // Should have been caught by constructor throw
        resolve(null);
        return;
      }

      const settle = (result: Transform | null) => {
        if (result === null) {
          transformInstance.terminate(); // Clean up if init failed
        }
        resolve(result);
      };

      const detachTempHandlers = () => {
        transformInstance.worker?.removeEventListener('message', tempOnMessage);
        transformInstance.worker?.removeEventListener('error', tempOnError);
      };

      const attachPersistentHandlers = () => {
        if (!transformInstance.worker) return;
        transformInstance.worker.onmessage =
          transformInstance._handleWorkerMessage.bind(transformInstance);
        transformInstance.worker.onerror =
          transformInstance._handleWorkerError.bind(transformInstance);
      };

      const tempOnError = (error: any) => {
        console.error(
          `Worker initialization error for transform '${spec.id}':`,
          error,
        );
        transformInstance.reportStatus({
          type: 'error',
          phase: 'init',
          message: error?.message ?? 'Worker initialization error',
        });
        transformInstance.workerReady = false;
        detachTempHandlers();
        settle(null);
      };

      const tempOnMessage = (event: MessageEvent<FromWorkerMessage>) => {
        if (event.data.type === 'init_ready') {
          // Modules imported (in parallel, inside the worker). Hand off to the
          // persistent handlers and compile the code through the same setCode
          // path warm recompiles use; setCode also runs the seed query that
          // populates `datasource` for consumers that read coordinate metadata
          // off the registry entry before their first query.
          detachTempHandlers();
          attachPersistentHandlers();
          transformInstance
            .setCode(transformInstance.code)
            .then((ok) => settle(ok ? transformInstance : null));
        } else if (event.data.type === 'init_error') {
          const data = event.data as WorkerInitErrorResponseMessage;
          console.error(
            `Worker explicit init_error for transform '${spec.id}':`,
            data.error,
          );
          transformInstance.reportStatus({
            type: 'error',
            phase: 'import',
            message: data.error,
            stack: data.stack,
          });
          transformInstance.workerReady = false;
          detachTempHandlers();
          settle(null);
        } else {
          // Should not receive query/code responses during module import
          console.warn(
            `Unexpected message type during init for ${spec.id}: ${event.data.type}`,
          );
        }
      };

      transformInstance.worker.addEventListener('message', tempOnMessage);
      transformInstance.worker.addEventListener('error', tempOnError);

      if (Object.keys(transformInstance.modules).length > 0) {
        transformInstance.reportStatus({ type: 'import-modules' });
      }
      const initMsg: WorkerInitMessage = {
        type: 'init',
        moduleMap: transformInstance.modules,
      };
      transformInstance.worker.postMessage(initMsg);
    });
  }

  /**
   * EID-126: (re)compile the transform's code in the LIVE worker — no
   * teardown, no module re-import (the generated module's static imports are
   * module-cache hits by URL). Resolves true once the new code version is
   * compiled and the seed query has re-run (refreshing `datasource`
   * metadata); false when the compile failed (an error status was reported)
   * or the call was superseded/terminated. On failure the previously
   * compiled code version stays live.
   *
   * NOTE for the data layer: after a successful setCode the registry entry
   * for this transform must be given a fresh identity (transform-code-proxy)
   * so consumer query caches keyed on source identity re-query.
   */
  public setCode(code: string): Promise<boolean> {
    if (!this.worker) return Promise.resolve(false);
    let built: TransformModuleSource;
    try {
      built = buildTransformModuleSource(code, this.modules);
    } catch (e: any) {
      console.error(
        `Transform '${this.id}' module source generation failed:`,
        e,
      );
      this.reportStatus({
        type: 'error',
        phase: 'init',
        message: e?.message ?? String(e),
      });
      return Promise.resolve(false);
    }
    const blob = new Blob([built.source], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    this.liveCodeUrls.add(url);
    const setCodeId = this.nextSetCodeId++;
    this.latestSetCodeId = setCodeId;
    this.reportStatus({ type: 'initialize' });
    return new Promise<boolean>((resolve) => {
      this.pendingSetCodes.set(setCodeId, {
        url,
        code,
        codeLineOffset: built.codeLineOffset,
        resolve,
      });
      const message: WorkerSetCodeMessage = {
        type: 'setCode',
        setCodeId,
        moduleUrl: url,
      };
      this.worker!.postMessage(message);
    });
  }

  // EID-126: worker reply to one setCode. Commit the latest successful
  // version (retiring the previous version's blob URL), discard everything
  // else, and report compile failures of the latest version as init errors
  // carrying the code line offset for editor marker mapping.
  private _handleSetCodeResponse(
    data:
      | WorkerCodeReadyResponseMessage
      | WorkerCodeErrorResponseMessage
      | WorkerCodeDiscardedResponseMessage,
  ): void {
    const pending = this.pendingSetCodes.get(data.setCodeId);
    if (!pending) return; // settled by terminate()
    this.pendingSetCodes.delete(data.setCodeId);

    if (data.type === 'code_ready' && data.setCodeId === this.latestSetCodeId) {
      if (this.codeModuleUrl) {
        URL.revokeObjectURL(this.codeModuleUrl);
        this.liveCodeUrls.delete(this.codeModuleUrl);
      }
      this.codeModuleUrl = pending.url;
      this.code = pending.code;
      this.codeLineOffset = pending.codeLineOffset;
      this.usesEvent = usesEventInput(pending.code);
      this.workerReady = true;
      this.reportStatus({ type: 'ready' });
      // Seed `datasource` so consumers that read coordinate metadata off the
      // registry entry (gridded/seasurface/track layers) have it available
      // before their first query — and, on a warm recompile, so the metadata
      // reflects the NEW code version. Best-effort: a transform whose output
      // is empty until the first interaction just leaves datasource null (its
      // consumers read coordinates from the query result instead).
      this.query({})
        .catch((e) =>
          console.warn(`Transform '${this.id}' seed query failed:`, e),
        )
        .then(() => pending.resolve(true));
      return;
    }

    // Superseded ready, discarded, or failed: this version never commits.
    URL.revokeObjectURL(pending.url);
    this.liveCodeUrls.delete(pending.url);
    if (data.type === 'code_error' && data.setCodeId === this.latestSetCodeId) {
      console.error(
        `Transform '${this.id}' code compilation failed:`,
        data.error,
        data.stack || '',
      );
      this.reportStatus({
        type: 'error',
        phase: 'init',
        message: data.error,
        stack: data.stack,
        codeLineOffset: pending.codeLineOffset,
      });
    }
    pending.resolve(false);
  }

  private async validateResult(result: any): Promise<void> {
    // The validator (and its schema set) is loaded lazily on first use (EID-63).
    const schemaValidator = await getSchemaValidator();
    if (this.outputType === 'dataset') {
      const valid = schemaValidator.validate(
        'https://schemas.oceanum.io/eidos/v0.12/data.json#/$defs/dataset',
        result,
      );
      if (!valid) {
        throw new Error(
          'Dataset validation failed: ' +
            schemaValidator.errorsText(schemaValidator.errors),
        );
      }
    } else if (this.outputType === 'geojson') {
      const valid = schemaValidator.validate(
        'https://schemas.oceanum.io/geojson.json#/definitions/FeatureCollection',
        result,
      );
      if (!valid) {
        throw new Error(
          'GeoJSON validation failed: ' +
            schemaValidator.errorsText(schemaValidator.errors),
        );
      }
    }
  }

  private _handleWorkerMessage(event: MessageEvent<FromWorkerMessage>): void {
    const data = event.data;
    if (
      data.type === 'code_ready' ||
      data.type === 'code_error' ||
      data.type === 'code_discarded'
    ) {
      this._handleSetCodeResponse(data);
      return;
    }
    if (data.type === 'query_response') {
      const pending = this.pendingQueries.get(data.queryId);
      if (pending) {
        clearTimeout(pending.timer);
        if (data.error) {
          console.error(
            `Error in transform worker for '${this.id}', query '${data.queryId}':`,
            data.error,
            data.details || '',
            data.stack || '',
          );
          this.reportStatus({
            type: 'error',
            phase: 'query',
            message: data.error,
            stack: data.stack,
            codeLineOffset: this.codeLineOffset,
          });
          this.pendingQueries.delete(data.queryId);
          pending.reject(new Error(data.error));
        } else {
          // EID-69: settle the pending entry only once validation completes.
          // Deleting synchronously here (before validateResult resolves) left
          // a window where teardown/error paths could not see the in-flight
          // query, and a late terminate() would not reject it.
          this.validateResult(data.result)
            .then(() => {
              this.pendingQueries.delete(data.queryId);
              // EID-126: a successful query returns the transform to ready
              // (recovering from per-query error phases only — the state
              // machine keeps init/import errors visible).
              this.reportStatus({ type: 'ready' });
              pending.resolve(data.result);
            })
            .catch((e) => {
              console.error(
                `Validation error in transform '${this.id}', query '${data.queryId}':`,
                e,
              );
              this.reportStatus({
                type: 'error',
                phase: 'validation',
                message: e?.message ?? String(e),
              });
              this.pendingQueries.delete(data.queryId);
              pending.reject(e);
            });
        }
      }
    } else {
      console.warn(
        `Transform '${this.id}': Unexpected message type received post-init: ${data.type}`,
      );
    }
  }

  private _handleWorkerError(error: any): void {
    console.error(
      `Unhandled error in transform worker for '${this.id}' post-init:`,
      error,
    );
    // EID-126: post-init unhandled worker errors surface while executing
    // queries; report before terminate() (which settles the pending maps).
    this.reportStatus({
      type: 'error',
      phase: 'query',
      message:
        error?.message ??
        'Transform worker encountered an unrecoverable error.',
    });
    this.workerReady = false;
    this.pendingQueries.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(
        new Error('Transform worker encountered an unrecoverable error.'),
      );
    });
    this.pendingQueries.clear();
    this.terminate();
  }

  public async query(queryObj: any, squeeze?: boolean): Promise<any> {
    if (!this.worker || !this.workerReady) {
      return Promise.reject(
        new Error(
          `Transform '${this.id}' worker is not initialized or not ready.`,
        ),
      );
    }

    const actualInputData: Record<string, any> = {};
    try {
      for (let i = 0; i < this.resolvedInputsMap.length; i++) {
        const dataContainer = this.resolvedInputsMap[i];
        // EID-125: each input is resolved with its own rewrite of the consumer
        // query (per-field: present → override, absent → inherit, null →
        // drop). $query inside the worker stays the original queryObj, and the
        // consumer query is still re-applied to the transform OUTPUT below —
        // widening an input window does not change downstream time semantics.
        const inputQuery = rewriteInputQuery(queryObj, this.inputSpecs[i]);
        const inputResult = await dataContainer.query(inputQuery);
        const sourceId = this.inputIds[i];
        if (!inputResult) {
          console.warn(
            `No data returned for transform '${this.id}' query from ${
              sourceId ?? `input ${i}`
            }`,
          );
          return Promise.resolve(null);
        }
        // Expose each input both positionally ($inputs[0]) and by its source id
        // ($inputs['my-source']) so transform code can use whichever it prefers.
        actualInputData[i] = inputResult;
        if (sourceId != null) actualInputData[sourceId] = inputResult;
      }
    } catch (error: any) {
      console.error(
        `Error fetching input data for transform '${this.id}' query:`,
        error,
      );
      this.reportStatus({
        type: 'error',
        phase: 'query',
        message: `Input data fetch failed: ${error?.message ?? error}`,
      });
      return Promise.reject(error);
    }

    // EID-86: expose the latest map interaction event as a reserved named key
    // on $inputs (positional data inputs use numeric keys). Always present
    // (null until the user interacts); transforms that don't read it ignore it.
    actualInputData['$event'] = this.getEvent ? this.getEvent() : null;

    const queryId = (this.nextQueryId++).toString();

    const rawResult: any = await new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingQueries.has(queryId)) {
          this.pendingQueries.delete(queryId);
          const message = `Query '${queryId}' for transform '${this.id}' timed out after ${this.QUERY_TIMEOUT_MS / 1000}s.`;
          this.reportStatus({ type: 'error', phase: 'timeout', message });
          reject(new Error(message));
        }
      }, this.QUERY_TIMEOUT_MS);

      this.pendingQueries.set(queryId, { resolve, reject, timer });

      const message: WorkerExecuteQueryMessage = {
        type: 'query',
        queryId,
        inputData: actualInputData,
        queryObj: queryObj,
        outputType: this.outputType,
      };
      this.worker!.postMessage(message);
    });

    if (rawResult == null) return null;

    // Wrap the (schema-validated) result into an inline Dataset so the transform
    // behaves like any other data source: `datasource` gives consumers the
    // coordinate metadata, and the returned value is the Dataset-normalised data
    // (rectilinear/mesh coord resolution, geo/time/level filter, squeeze).
    // DataWrapper.inline handles both dataset (Dataset.init) and geojson
    // (Dataset.fromGeojson) outputs; an output with no usable data (e.g. an
    // empty geojson FeatureCollection before the first map interaction) yields a
    // null datasource and a null result rather than throwing.
    try {
      const wrapper = await DataWrapper.inline(this.id, rawResult);
      this.datasource = wrapper.datasource;
      return await wrapper.query(queryObj, squeeze);
    } catch (e) {
      console.warn(
        `Transform '${this.id}': could not wrap result into a Dataset`,
        e,
      );
      return null;
    }
  }

  public terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    if (this.workerUrl) {
      URL.revokeObjectURL(this.workerUrl);
      this.workerUrl = null;
    }
    // EID-126: release every unrevoked code-module blob URL (the committed
    // version and any in-flight compiles).
    for (const url of this.liveCodeUrls) URL.revokeObjectURL(url);
    this.liveCodeUrls.clear();
    this.codeModuleUrl = null;
    this.workerReady = false; // Ensure ready state is false
    this.pendingQueries.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(new Error(`Transform '${this.id}' is terminating.`));
    });
    this.pendingQueries.clear();
    // EID-126: settle in-flight recompiles (resolve false, not reject —
    // setCode resolves a boolean).
    this.pendingSetCodes.forEach((pending) => pending.resolve(false));
    this.pendingSetCodes.clear();
  }
}
